// S12 — admin-facing entity type catalog. System types are read-only.
import { useState } from "react";

import { useCreateEntityType, useEntityTypes } from "@/api/entities";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function EntityTypeManagerPage() {
  // A 500 renders an EMPTY CATALOG with no message, under a page whose whole
  // purpose is to show which entity types exist. An admin reads "there are no
  // types" and creates a duplicate of one that already exists.
  const {
    data: types = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useEntityTypes();
  const create = useCreateEntityType();
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("");
  const [parents, setParents] = useState("");

  async function handleCreate() {
    if (!name.trim()) return;
    await create.mutateAsync({
      name: name.trim(),
      icon: icon || null,
      allowed_parent_types: parents
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    });
    setName("");
    setIcon("");
    setParents("");
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Entity Types</h1>
        <p className="text-sm text-muted-foreground">
          System types (Bank / Region / Zone / Branch / Floor) ship with
          every tenant. Add custom types for your hierarchy.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Icon (lucide name)</Label>
              <Input value={icon} onChange={(e) => setIcon(e.target.value)} />
            </div>
            <div>
              <Label>Allowed parent types (comma-separated)</Label>
              <Input
                value={parents}
                onChange={(e) => setParents(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleCreate} disabled={create.isPending}>
            Add type
          </Button>
        </CardContent>
      </Card>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        label="the entity type catalog"
        onRetry={() => void refetch()}
        isEmpty={types.length === 0}
        emptyMessage="No entity types defined yet."
      >
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Icon</th>
                <th className="px-3 py-2 text-left">Allowed parents</th>
                <th className="px-3 py-2 text-left">System</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2">{t.icon ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {(t.allowed_parent_types ?? []).join(", ") || "—"}
                  </td>
                  <td className="px-3 py-2">
                    {t.is_system && <Badge variant="online">system</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </QueryState>
    </div>
  );
}
